<template>
  <div class="h-full flex flex-col rounded-2xl overflow-hidden bg-white dark:bg-dark-800 border border-blue-200 dark:border-blue-800 shadow-sm">
    <!-- Header -->
    <div class="p-4 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 dark:from-blue-500/20 dark:to-indigo-500/20 border-b border-blue-200/50 dark:border-blue-800/30">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
            <i class="fas fa-shopping-cart"></i>
          </div>
          <div>
            <h3 class="text-sm font-bold text-gray-900 dark:text-white">
              Orders
            </h3>
            <p class="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
              <span>{{ orderDetails?.length || 0 }} total</span>
              <span
                v-if="pendingApprovals > 0"
                class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold"
              >
                <i class="fas fa-clock mr-1"></i>{{ pendingApprovals }}
              </span>
            </p>
          </div>
        </div>
        <div class="text-right">
          <p class="text-xs text-gray-600 dark:text-gray-400 mb-0.5">Total Revenue</p>
          <p class="text-lg font-black bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
            {{ formatCurrency(orderDetails?.reduce((sum, o) => sum + Number(o.total_amount), 0) || 0) }}
          </p>
        </div>
      </div>
    </div>
    <!-- Content -->
    <div class="flex-1 overflow-hidden p-3">
      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center h-full">
        <div class="relative">
          <div class="w-10 h-10 rounded-full border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 animate-spin"></div>
          <i class="fas fa-shopping-cart absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-blue-600 dark:text-blue-400 text-sm"></i>
        </div>
        <p class="mt-2 text-xs font-medium text-gray-600 dark:text-gray-400">Loading orders...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="!orderDetails || orderDetails.length === 0" class="flex flex-col items-center justify-center h-full text-center">
        <div class="w-16 h-16 rounded-2xl bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center mb-3">
          <i class="fas fa-inbox text-2xl text-blue-600 dark:text-blue-400"></i>
        </div>
        <p class="text-xs font-semibold text-gray-900 dark:text-white">No orders found</p>
        <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">No orders for this date</p>
      </div>

      <!-- Orders List -->
      <div v-else class="h-full overflow-y-auto custom-scrollbar space-y-1.5">
        <div v-for="order in orderDetails" :key="order.order_id" 
             class="group p-2.5 rounded-lg bg-white/50 dark:bg-dark-700/50 border border-blue-200/30 dark:border-blue-800/20 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md transition-all duration-200 cursor-pointer"
             @click="openOrder(order.order_id)">
          <div class="flex items-center justify-between gap-2">
            <div class="flex-1 min-w-0">
              <p class="text-xs font-semibold text-blue-600 dark:text-blue-400 group-hover:underline mb-0.5">
                {{ order.order_number }}
              </p>
              <p class="text-[10px] text-gray-600 dark:text-gray-400 truncate">
                {{ order.client_name }}
              </p>
            </div>
            <div class="text-xs font-bold text-gray-900 dark:text-white whitespace-nowrap">
              {{ formatCurrency(order.total_amount) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { formatCurrency } from '@/utils/formatters';

const props = defineProps({
  orderDetails: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  pendingApprovals: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['open-order']);

const openOrder = (orderId) => {
  emit('open-order', orderId);
};

const truncateText = (text, length = 15) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};
</script> 